/**
 * Auto-detect the most appropriate prayer calculation method
 * for the user's geographic location.
 *
 * Two input paths are supported:
 *   1. The user picked a city from the dropdown → we use the city's `country_en`.
 *   2. The user is using GPS only → we use a bounding-box fallback over lat/lon.
 *
 * Mapping is based on the official methods adopted by each region's religious
 * authorities, mirroring the defaults used by Muslim Pro / Athan / Salatuk:
 *   • Saudi Arabia + Gulf       → Makkah (Umm al-Qura)
 *   • Egypt + Levant + N. Africa → Egypt (Egyptian General Authority of Survey)
 *   • Pakistan + India + B'desh → Karachi (Univ. of Islamic Sciences, Karachi)
 *   • USA + Canada              → ISNA
 *   • Rest of world             → MWL (Muslim World League)
 */

// Map of country_en (matches cities.js exactly) → method key
const COUNTRY_TO_METHOD = {
  // ── Gulf → Umm al-Qura
  'Saudi Arabia': 'Makkah',
  'UAE':          'Makkah',
  'Qatar':        'Makkah',
  'Kuwait':       'Makkah',
  'Bahrain':      'Makkah',
  'Oman':         'Makkah',
  'Yemen':        'Makkah',

  // ── Egypt + Levant + North Africa → Egyptian
  'Egypt':       'Egypt',
  'Jordan':      'Egypt',
  'Palestine':   'Egypt',
  'Lebanon':     'Egypt',
  'Syria':       'Egypt',
  'Iraq':        'Egypt',
  'Sudan':       'Egypt',
  'Libya':       'Egypt',
  'Tunisia':     'Egypt',
  'Algeria':     'Egypt',
  'Morocco':     'Egypt',
  'Mauritania':  'Egypt',

  // ── South Asia → Karachi
  'Pakistan':    'Karachi',
  'India':       'Karachi',
  'Bangladesh':  'Karachi',
  'Afghanistan': 'Karachi',
  'Iran':        'Karachi',
  'Sri Lanka':   'Karachi',

  // ── North America → ISNA
  'USA':         'ISNA',
  'United States': 'ISNA',
  'Canada':      'ISNA',
  'Mexico':      'ISNA',

  // ── Everything else explicitly falls back to MWL
};

// Friendly country label (bilingual) for display, used when we know the country
export const getCountryLabel = (city, lang) => {
  if (!city) return null;
  return lang === 'ar' ? (city.country_ar || city.country_en) : city.country_en;
};

/**
 * Pick a method based on `country_en` (exact string from cities.js).
 * Returns a method key OR 'MWL' as final fallback.
 */
export const methodFromCountry = (country_en) => {
  if (!country_en) return 'MWL';
  return COUNTRY_TO_METHOD[country_en] || 'MWL';
};

/**
 * Approximate country/region detection from raw GPS coordinates.
 * Uses simple bounding boxes — accurate to the level needed for picking a
 * prayer-calculation method (not for precise reverse geocoding).
 *
 * Returns a method key directly.
 */
export const methodFromCoords = (lat, lon) => {
  if (lat == null || lon == null) return 'MWL';

  // Gulf / Saudi Peninsula  (incl. Yemen, Oman)
  if (lat >= 12 && lat <= 33 && lon >= 34 && lon <= 60) return 'Makkah';

  // North America
  if (lat >= 15 && lat <= 72 && lon >= -170 && lon <= -50) return 'ISNA';

  // South Asia (Iran → Bangladesh)
  if (lat >= 5 && lat <= 40 && lon >= 60 && lon <= 95) return 'Karachi';

  // Egypt + Levant + North Africa  (broad lon range to cover Morocco→Iraq)
  // Note: this excludes the Gulf box above (checked first).
  if (lat >= 14 && lat <= 38 && lon >= -18 && lon <= 50) return 'Egypt';

  // Sub-Saharan & elsewhere → default MWL
  return 'MWL';
};

/**
 * Top-level helper.
 * Priority: city's country (if a city is selected) → coords → 'MWL'.
 */
export const autoPickMethod = ({ selectedCity, lat, lon }) => {
  if (selectedCity && selectedCity.country_en) {
    return methodFromCountry(selectedCity.country_en);
  }
  return methodFromCoords(lat, lon);
};
